// TableDetails.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  memo,
} from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  Users,
  Mic2,
  ArrowLeft,
  ArrowRight,
  ArrowRightToLine,
  LogOut,
  RefreshCw,
  Star,
  ListChecks,
} from "lucide-react";
import { toast } from "sonner";

import {
  getTableParticipants,
  leaveSessionTable,
  leaveSessionTableKeepalive,
  joinSessionTable,
} from "@/Services/EventMeetingServices";

const film = "https://placehold.net/200x200.png?text=Table+Icon";

// ---------------------------------------------------------------------------
// CONSTANTS
// ---------------------------------------------------------------------------

const SEAT_LABELS_8 = ["A", "B", "C", "D", "E", "F", "G", "H"];

// ---------------------------------------------------------------------------
// PURE UTILITIES
// ---------------------------------------------------------------------------

const joinCacheKey = (sessionId, tableId) =>
  `livekitJoin:${String(sessionId)}:${String(tableId)}`;

const getTableId = (t) => t?._id ?? t?.id ?? t?.tableId ?? null;
const getUserId = (u) => u?._id ?? u?.id ?? u?.userId ?? u?.memberId ?? null;

const getName = (u) =>
  u?.name ??
  u?.fullName ??
  u?.userName ??
  u?.username ??
  u?.email ??
  "Participant";

const getAvatarUrl = (u) =>
  u?.avatar ?? u?.profileImage ?? u?.profilePic ?? u?.image ?? null;

const getRole = (u) => u?.role ?? u?.userType ?? u?.type ?? null;

const getInitials = (name) => {
  const s = String(name || "").trim();
  if (!s) return "NA";
  const parts = s.split(/\s+/).filter(Boolean);
  const a = parts[0]?.[0] || "";
  const b = parts.length > 1 ? parts[1]?.[0] || "" : parts[0]?.[1] || "";
  return (a + b).toUpperCase();
};

const stripHtml = (html) =>
  html
    ? String(html)
        .replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim()
    : "";

/**
 * Calculates (x, y) positions around a circle for seat rendering.
 */
function seatPositions(count, radius = 100) {
  const items = [];
  for (let i = 0; i < count; i++) {
    const angle = (i / count) * Math.PI * 2 - Math.PI / 2;
    items.push({
      x: Math.cos(angle) * radius,
      y: Math.sin(angle) * radius,
      angle,
    });
  }
  return items;
}

// ---------------------------------------------------------------------------
// STAT CARD
// ---------------------------------------------------------------------------

const StatCard = memo(({ icon: Icon, iconColor, value, label, testId }) => (
  <div
    className="glass rounded-xl p-4 flex items-center gap-3"
    data-testid={testId}
  >
    <div
      className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${iconColor}`}
    >
      <Icon size={18} aria-hidden="true" />
    </div>
    <div>
      <p className="font-display text-xl tracking-tight leading-none">
        {value}
      </p>
      <p className="text-[10px] text-white/55 mt-0.5">{label}</p>
    </div>
  </div>
));
StatCard.displayName = "StatCard";

// ---------------------------------------------------------------------------
// OCCUPANCY BAR
// ---------------------------------------------------------------------------

const OccupancyBar = memo(({ filled, capacity, loading, error }) => {
  const pct = useMemo(() => {
    const cap = Math.max(1, Number(capacity) || 8);
    return Math.min(100, Math.round((filled / cap) * 100));
  }, [filled, capacity]);

  return (
    <div className="glass rounded-xl p-4" data-testid="table-details-occupancy">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-medium text-white/80">Seat Occupancy</p>
        <p className="text-xs text-white/55">
          {filled}/{capacity} filled
        </p>
      </div>

      <div className="h-2 rounded-full bg-white/[0.06] overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{
            width: `${pct}%`,
            background:
              pct >= 90
                ? "linear-gradient(90deg, #C6A87C, #D9BC93)"
                : pct >= 60
                  ? "linear-gradient(90deg, #7BC8A6, #C6A87C)"
                  : "linear-gradient(90deg, #7BC8A6, #2A7E5D)",
          }}
          data-testid="table-details-progress-fill"
        />
      </div>

      {loading && (
        <p className="text-xs text-white/40 mt-2 flex items-center gap-1.5">
          <RefreshCw size={11} className="animate-spin" aria-hidden="true" />
          Loading participants…
        </p>
      )}
      {!loading && error && (
        <p className="text-xs text-red-400/80 mt-2">{String(error)}</p>
      )}
    </div>
  );
});
OccupancyBar.displayName = "OccupancyBar";

// ---------------------------------------------------------------------------
// AGENDA CARD
// ---------------------------------------------------------------------------

const AgendaCard = memo(({ agenda }) => {
  if (!agenda) return null;
  return (
    <div className="glass rounded-2xl p-6" data-testid="table-details-agenda">
      <div className="flex items-center gap-2 mb-3">
        <ListChecks size={15} className="text-[#C6A87C]" aria-hidden="true" />
        <p className="overline">Table Agenda</p>
      </div>
      <p className="text-white/70 text-sm leading-relaxed">{agenda}</p>
    </div>
  );
});
AgendaCard.displayName = "AgendaCard";

// ---------------------------------------------------------------------------
// 3-D ROUND TABLE WITH SEATS
// ---------------------------------------------------------------------------

const RoundTable3D = memo(({ seats, tableIcon, tableName, onSeatClick }) => {
  const positions = useMemo(() => seatPositions(8, 100), []);

  return (
    <div
      className="relative mx-auto w-[300px] h-[280px]"
      style={{ perspective: "900px" }}
      aria-label="Round table seating diagram"
      role="img"
    >
      {/* 3-D surface layer */}
      <div
        className="absolute inset-0"
        style={{
          transformStyle: "preserve-3d",
          transform: "rotateX(45deg)",
        }}
        aria-hidden="true"
      >
        {/* Shadow disc */}
        <div
          className="absolute"
          style={{
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%) translateZ(-12px)",
            width: 220,
            height: 220,
            borderRadius: "50%",
            background: "rgba(0,0,0,0.4)",
            filter: "blur(22px)",
          }}
        />

        {/* Table surface */}
        <div
          className="absolute"
          style={{
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            width: 220,
            height: 220,
            borderRadius: "50%",
            background:
              "radial-gradient(circle at 35% 30%, #E7D9C0 0%, #C6A87C 35%, #8A6F4A 75%, #5A4530 100%)",
            boxShadow:
              "inset 0 0 0 2px rgba(60,40,20,0.45), inset 0 -6px 18px rgba(0,0,0,0.35), 0 6px 14px rgba(0,0,0,0.5)",
            border: "1px solid rgba(0,0,0,0.4)",
          }}
        />

        {/* Inner decorative ring */}
        <div
          className="absolute"
          style={{
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%) translateZ(0.5px)",
            width: 160,
            height: 160,
            borderRadius: "50%",
            border: "1px solid rgba(60,40,20,0.35)",
            opacity: 0.7,
          }}
        />

        {/* Centre plate */}
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%) translateZ(1px)",
            width: 96,
            height: 96,
            borderRadius: "50%",
            background: tableIcon
              ? "radial-gradient(circle at 35% 30%, #fdf6e8 0%, #f3e6cd 50%, #d9c599 100%)"
              : "rgba(20,12,4,0.35)",
            border: tableIcon
              ? "1px solid rgba(60,40,20,0.45)"
              : "1.5px dashed rgba(255,255,255,0.18)",
            boxShadow: tableIcon ? "inset 0 -2px 6px rgba(0,0,0,0.25)" : "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          {tableIcon ? (
            <img
              src={tableIcon}
              alt={tableName}
              style={{
                maxWidth: "72%",
                maxHeight: "72%",
                objectFit: "contain",
                filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.3))",
              }}
            />
          ) : (
            <span
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: 13,
                color: "rgba(60,40,20,0.85)",
                padding: "0 8px",
                lineHeight: 1.1,
                textAlign: "center",
              }}
            >
              {tableName}
            </span>
          )}
        </div>

        {/* Chairs */}
        {positions.map((p, i) => (
          <div
            key={i}
            className="absolute"
            style={{
              left: "50%",
              top: "50%",
              transform: `translate(-50%, -50%) translate(${p.x}px, ${p.y}px) translateZ(6px) rotate(${(p.angle * 180) / Math.PI + 90}deg)`,
            }}
          >
            {/* Chair seat */}
            <div
              style={{
                width: 26,
                height: 20,
                borderRadius: "6px 6px 4px 4px",
                background: "linear-gradient(180deg, #2a3a36 0%, #1a2522 100%)",
                border: "1px solid rgba(255,255,255,0.08)",
                boxShadow:
                  "0 2px 4px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.06)",
              }}
            />
            {/* Chair back */}
            <div
              style={{
                position: "absolute",
                left: "50%",
                top: -2,
                transform: "translateX(-50%) rotateX(-50deg)",
                transformOrigin: "bottom center",
                width: 22,
                height: 13,
                borderRadius: "4px 4px 2px 2px",
                background: "linear-gradient(180deg, #3a4a45 0%, #1f2c28 100%)",
                border: "1px solid rgba(255,255,255,0.08)",
              }}
            />
          </div>
        ))}
      </div>

      {/* Avatar / label layer */}
      <div className="absolute inset-0" aria-hidden="true">
        {positions.map((p, i) => {
          const occupant = seats[i] ?? null;
          const label = SEAT_LABELS_8[i] ?? String(i + 1);
          const name = occupant ? stripHtml(getName(occupant)) : "";
          const avatarUrl = occupant ? getAvatarUrl(occupant) : null;
          const initials = name ? getInitials(name) : label;
          const isEmpty = !occupant;

          return (
            <div
              key={i}
              className="absolute top-1/2 left-1/2"
              style={{
                transform: `translate(${p.x - 16}px, ${p.y * 0.72 - 42}px)`,
              }}
            >
              {isEmpty ? (
                <div
                  className="w-8 h-8 rounded-full border-2 border-dashed border-white/20 flex items-center justify-center"
                  title={`Seat ${label} — open`}
                  aria-label={`Seat ${label} open`}
                >
                  <span className="text-[9px] text-white/30 font-semibold">
                    {label}
                  </span>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => onSeatClick?.(name)}
                  className="w-8 h-8 rounded-full overflow-hidden border-2 border-white/30 hover:border-[#C6A87C] hover:scale-110 transition-all shadow-lg focus:outline-none focus:ring-2 focus:ring-[#C6A87C]/50"
                  style={{
                    animationDelay: `${i * 0.25}s`,
                    boxShadow: "0 3px 10px -2px rgba(0,0,0,0.55)",
                  }}
                  title={name}
                  aria-label={`Seat ${label}: ${name}`}
                  data-testid={`table-details-seat-${i}`}
                >
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt={name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="w-full h-full flex items-center justify-center text-[10px] font-semibold bg-[#0A1D1A] text-[#C6A87C]">
                      {initials}
                    </span>
                  )}
                </button>
              )}

              <div className="flex justify-center mt-0.5">
                <span
                  className={`text-[8px] font-bold px-1 rounded ${
                    isEmpty ? "text-white/20" : "text-[#C6A87C]"
                  }`}
                >
                  {label}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});
RoundTable3D.displayName = "RoundTable3D";

// ---------------------------------------------------------------------------
// PARTICIPANT LIST ROWS
// ---------------------------------------------------------------------------

const ParticipantRow = memo(({ occupant, index, onSeatClick }) => {
  const name = stripHtml(getName(occupant));
  const avatarUrl = getAvatarUrl(occupant);
  const initials = getInitials(name);
  const label = SEAT_LABELS_8[index] ?? String(index + 1);

  return (
    <button
      type="button"
      onClick={() => onSeatClick?.(name)}
      className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-white/[0.04] transition-colors text-left group"
      data-testid={`participant-row-${index}`}
    >
      <span className="w-6 h-6 rounded-full bg-[#C6A87C]/15 border border-[#C6A87C]/30 text-[#C6A87C] text-[10px] font-bold flex items-center justify-center shrink-0">
        {label}
      </span>

      <div className="w-8 h-8 rounded-full overflow-hidden border border-white/20 shrink-0">
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={name}
            className="w-full h-full object-cover"
          />
        ) : (
          <span className="w-full h-full flex items-center justify-center text-[10px] font-semibold bg-[#0A1D1A] text-[#C6A87C]">
            {initials}
          </span>
        )}
      </div>

      <span className="text-sm text-white/80 group-hover:text-white transition-colors truncate flex-1">
        {name}
      </span>

      <ArrowRight
        size={13}
        className="text-white/20 group-hover:text-[#C6A87C] transition-colors shrink-0"
        aria-hidden="true"
      />
    </button>
  );
});
ParticipantRow.displayName = "ParticipantRow";

const EmptySeatRow = memo(({ index }) => {
  const label = SEAT_LABELS_8[index] ?? String(index + 1);
  return (
    <div
      className="flex items-center gap-3 p-3 rounded-xl opacity-40"
      data-testid={`empty-seat-row-${index}`}
    >
      <span className="w-6 h-6 rounded-full border border-dashed border-white/20 text-white/30 text-[10px] font-bold flex items-center justify-center shrink-0">
        {label}
      </span>
      <div className="w-8 h-8 rounded-full border-2 border-dashed border-white/15" />
      <span className="text-sm text-white/30 italic">Open seat</span>
    </div>
  );
});
EmptySeatRow.displayName = "EmptySeatRow";

// ---------------------------------------------------------------------------
// MAIN TABLE DETAILS PAGE
// ---------------------------------------------------------------------------

export default function TableDetails() {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    eventId,
    sessionId: paramSessionId,
    tableId: paramTableId,
  } = useParams();

  const sessionId = paramSessionId || location.state?.sessionId || null;
  const tableId = paramTableId || location.state?.tableId || null;

  // State
  const [participants, setParticipants] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [leaveLoading, setLeaveLoading] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [joinLoading, setJoinLoading] = useState(false);

  // Location-derived data
  const sessionFromState = location.state?.session || null;
  const hostsFromSession = sessionFromState?.hosts || [];
  const speakersFromSession =
    sessionFromState?.speakers || sessionFromState?.speaker || [];

  const tableIconFromState = participants?.[0]?.tableIcon || null;

  const tableName = stripHtml(
    location.state?.tableName ||
      participants?.[0]?.tableName ||
      "Table Discussion",
  );
  const tableCapacity =
    location.state?.capacity ?? participants?.[0]?.capacity ?? 8;

  const tableIcon = tableIconFromState || participants?.[0]?.tableIcon || film;
  const tableAgenda =
    location.state?.agenda || participants?.[0]?.agenda || null;

  const capacity = Math.max(1, Number(tableCapacity) || 8);

  // Page title
  useEffect(() => {
    document.title = `${tableName} | The Global App`;
  }, [tableName]);

  // Silent background poll
  useEffect(() => {
    if (!sessionId || !tableId) return;

    let cancelled = false;
    let intervalId = null;

    const poll = async () => {
      try {
        const res = await getTableParticipants(sessionId, tableId);

        if (cancelled) return;

        const raw = res?.data;

        const tableMetadata = {
          tableIcon: raw?.sponsorDetails?.image || raw?.tableIcon || null,
          tableName: raw?.tableName || null,
          capacity: raw?.capacity || raw?.currentOccupancy || null,
          agenda: raw?.agenda || null,
        };

        const result = Array.isArray(raw?.participants)
          ? raw.participants
          : Array.isArray(res?.participants)
            ? res.participants
            : [];

        if (result.length > 0 || tableMetadata.tableName) {
          setParticipants((prev) => {
            const prevIds = prev.map((p) => getUserId(p)).join(",");
            const nextIds = result.map((p) => getUserId(p)).join(",");
            if (
              prevIds === nextIds &&
              prev[0]?.tableName === tableMetadata.tableName
            )
              return prev;

            const enriched =
              result.length > 0
                ? result.map((p, i) =>
                    i === 0 ? { ...p, ...tableMetadata } : p,
                  )
                : [{ ...tableMetadata }];

            return enriched;
          });
          setError(null);
        }
      } catch (e) {
        if (cancelled) return;
        if (initialLoading) {
          setError(e?.message || "Failed to load participants.");
        }
      } finally {
        if (!cancelled) setInitialLoading(false);
      }
    };

    poll();
    intervalId = setInterval(poll, 3000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [sessionId, tableId, refreshTick]);

  const handleRefresh = useCallback(() => {
    setInitialLoading(true);
    setRefreshTick((t) => t + 1);
  }, []);

  // Leave logic
  const didLeaveRef = useRef(false);

  const leaveTableOnce = useCallback(async () => {
    if (didLeaveRef.current) return;
    if (!sessionId) return;

    didLeaveRef.current = true;
    setLeaveLoading(true);

    try {
      await leaveSessionTable(sessionId);
    } catch (e) {
      console.error("Failed to leave table:", e);
    } finally {
      setLeaveLoading(false);
      if (sessionId && tableId) {
        sessionStorage.removeItem(joinCacheKey(sessionId, tableId));
      }
    }
  }, [sessionId, tableId]);

  // ✅ Tab close / refresh confirmation + best-effort leave API
  useEffect(() => {
    if (!sessionId) return;

    // 1) Ask confirmation when user tries to close/refresh/navigate away
    const onBeforeUnload = (e) => {
      if (didLeaveRef.current) return;
      e.preventDefault();
      e.returnValue = ""; // required for Chrome to show the prompt
    };

    // 2) Only runs if the page is actually unloading (user clicked "Leave")
    const onPageHide = () => {
      if (didLeaveRef.current) return;
      didLeaveRef.current = true;

      // best-effort leave call that can run during unload
      leaveSessionTableKeepalive(sessionId);

      // cleanup join cache
      if (sessionId && tableId) {
        sessionStorage.removeItem(joinCacheKey(sessionId, tableId));
      }
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    window.addEventListener("pagehide", onPageHide);

    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [sessionId, tableId]);

  const autoLeaveHandledRef = useRef(false);
  useEffect(() => {
    const tokenMissing = location.state?.tokenMissing === true;
    if (!tokenMissing || autoLeaveHandledRef.current || !sessionId) return;
    autoLeaveHandledRef.current = true;
    (async () => {
      await leaveTableOnce();
      navigate(`/events/${eventId}/sessions/${sessionId}/tables/${tableId}/`, {
        replace: true,
      });
    })();
  }, [location.state, sessionId, leaveTableOnce, navigate, eventId, tableId]);

  useEffect(() => {
    const onPopState = () => leaveTableOnce();
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [leaveTableOnce]);

  useEffect(() => {
    if (!location.state?.autoOpenJoin) return;
    if (!eventId || !sessionId || !tableId) return;
    navigate(
      `/events/${eventId}/sessions/${sessionId}/tables/${tableId}/join`,
      {
        replace: true,
        state: {
          tableName,
          tableIcon,
          capacity,
          session: sessionFromState,
        },
      },
    );
  }, [
    location.state,
    navigate,
    eventId,
    sessionId,
    tableId,
    tableName,
    tableIcon,
    capacity,
    sessionFromState,
  ]);

  // Navigation handlers
  const handleBack = useCallback(async () => {
    await leaveTableOnce();
    navigate(`/lobby?event=${eventId}&session=${sessionId}`, { replace: true });
  }, [leaveTableOnce, navigate, eventId, sessionId]);

  const handleLeave = useCallback(async () => {
    await leaveTableOnce();
    navigate(`/lobby?event=${eventId}&session=${sessionId}`, { replace: true });
  }, [leaveTableOnce, navigate, eventId, sessionId]);

  const handleJoin = useCallback(async () => {
    if (!eventId || !sessionId || !tableId) return;

    setJoinLoading(true);
    try {
      const resp = await joinSessionTable(sessionId, tableId);

      sessionStorage.setItem(
        joinCacheKey(sessionId, tableId),
        JSON.stringify(resp?.data ?? resp),
      );

      navigate(
        `/events/${eventId}/sessions/${sessionId}/tables/${tableId}/join`,
        {
          state: {
            tableName,
            tableIcon,
            capacity,
            session: sessionFromState,
            joinResponse: resp?.data ?? resp,
          },
        },
      );
    } catch (e) {
      toast.error(e?.message || "Failed to join table. Please try again.");
    } finally {
      setJoinLoading(false);
    }
  }, [
    navigate,
    eventId,
    sessionId,
    tableId,
    tableName,
    tableIcon,
    capacity,
    sessionFromState,
  ]);

  // Derived participant data
  const regularParticipants = useMemo(() => {
    const p = Array.isArray(participants) ? participants : [];

    const hostIds = new Set(
      (hostsFromSession || []).map(getUserId).filter(Boolean).map(String),
    );
    const speakerIds = new Set(
      (speakersFromSession || []).map(getUserId).filter(Boolean).map(String),
    );

    return p.filter((u) => {
      const id = getUserId(u);
      if (id && (hostIds.has(String(id)) || speakerIds.has(String(id))))
        return false;
      const role = String(getRole(u) || "").toLowerCase();
      return role !== "host" && role !== "speaker";
    });
  }, [participants, hostsFromSession, speakersFromSession]);

  const seats = useMemo(() => {
    const arr = Array.from({ length: 8 }, () => null);
    for (let i = 0; i < regularParticipants.length && i < 8; i++) {
      arr[i] = regularParticipants[i];
    }
    return arr;
  }, [regularParticipants]);

  const filled = regularParticipants.length;
  const seatsLeft = Math.max(0, capacity - filled);
  const hostsCount = (hostsFromSession || []).length;
  const speakersCount = (speakersFromSession || []).length;
  const canJoin =
    !leaveLoading && !joinLoading && !!eventId && !!sessionId && !!tableId;

  const handleSeatClick = useCallback((name) => {
    toast(`Viewing profile: ${name}`);
  }, []);

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------
  return (
    <div className="min-h-screen">
      {/* ── HERO SECTION ── */}
      <section
        className="border-b border-white/[0.06]"
        style={{
          background: "linear-gradient(180deg, #061412 0%, #0A1D1A 100%)",
        }}
      >
        <div className="container-x py-12 md:py-16">
          {/* Back button */}
          <button
            type="button"
            onClick={handleBack}
            disabled={leaveLoading}
            className="mb-6 inline-flex items-center gap-2 text-xs text-white/55 hover:text-white/90 transition-colors disabled:opacity-40"
            aria-label="Back to tables"
            data-testid="table-details-back"
          >
            <ArrowLeft size={14} aria-hidden="true" />
            Back to tables
          </button>

          <div className="grid lg:grid-cols-12 gap-8 items-start">
            {/* Left: Title + Live badge */}
            <div className="lg:col-span-5">
              <div className="mb-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#2A7E5D]/15 border border-[#2A7E5D]/40">
                <span
                  className="w-1.5 h-1.5 rounded-full bg-[#7BC8A6] ring-live"
                  aria-hidden="true"
                />
                <span className="overline text-[#7BC8A6]">Live now</span>
              </div>

              <h1 className="font-display font-light text-4xl md:text-5xl tracking-tighter leading-[0.95] mb-3">
                {tableName}
              </h1>

              <p className="text-white/55 text-sm flex items-center gap-2">
                <Users
                  size={13}
                  className="text-[#C6A87C]"
                  aria-hidden="true"
                />
                {filled} of {capacity} seats filled ·{" "}
                <span className="text-[#7BC8A6]">{seatsLeft} open</span>
              </p>
            </div>

            {/* Right: Stats + Occupancy */}
            <div className="lg:col-span-7 space-y-4">
              {/* Refresh control */}
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleRefresh}
                  disabled={initialLoading}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/[0.12] text-xs text-white/70 hover:border-white/30 hover:text-white transition-colors disabled:opacity-40"
                  aria-label="Refresh participant list"
                  data-testid="table-details-refresh"
                >
                  <RefreshCw
                    size={13}
                    className={initialLoading ? "animate-spin" : ""}
                    aria-hidden="true"
                  />
                  {initialLoading ? "Refreshing…" : "Refresh"}
                </button>
              </div>
              {/* Stats row */}
              <div
                className="grid grid-cols-1 sm:grid-cols-3 gap-3"
                data-testid="table-details-stats"
              >
                <StatCard
                  icon={Users}
                  iconColor="bg-blue-500/15 text-blue-400"
                  value={`${Math.min(filled, capacity)}/${capacity}`}
                  label="Seats filled"
                  testId="table-details-stat-seats"
                />
                <StatCard
                  icon={Star}
                  iconColor="bg-[#C6A87C]/15 text-[#C6A87C]"
                  value={hostsCount}
                  label="Hosts"
                  testId="table-details-stat-hosts"
                />
                <StatCard
                  icon={Mic2}
                  iconColor="bg-purple-500/15 text-purple-400"
                  value={speakersCount}
                  label="Speakers"
                  testId="table-details-stat-speakers"
                />
              </div>

              {/* Occupancy bar */}
              <OccupancyBar
                filled={filled}
                capacity={capacity}
                loading={initialLoading}
                error={error}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── MAIN CONTENT ── */}
      <section className="section">
        <div className="container-x">
          {/* ── Action buttons (above table visual) ── */}
          <div
            className="flex flex-col sm:flex-row gap-4 mb-8"
            data-testid="table-details-actions"
          >
            <button
              type="button"
              onClick={handleJoin}
              disabled={!canJoin}
              data-testid="table-details-join"
              className="flex-1 flex items-center justify-center gap-3 px-6 py-4 rounded-2xl bg-[#C6A87C] text-[#061412] font-semibold hover:bg-[#D9BC93] transition-colors disabled:opacity-50 disabled:cursor-not-allowed group"
            >
              <ArrowRightToLine
                size={18}
                className="group-hover:translate-x-0.5 transition-transform"
                aria-hidden="true"
              />
              <span>Join Table Discussion</span>
              {seatsLeft > 0 && (
                <span className="ml-auto text-[#061412]/60 text-xs font-normal">
                  {seatsLeft} seat{seatsLeft !== 1 ? "s" : ""} left
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={handleLeave}
              disabled={leaveLoading}
              data-testid="table-details-leave"
              className="flex items-center justify-center gap-2 px-6 py-4 rounded-2xl border border-white/[0.12] text-white/70 hover:border-white/30 hover:text-white transition-colors disabled:opacity-40"
            >
              <LogOut size={16} aria-hidden="true" />
              {leaveLoading ? "Leaving…" : "Leave Table"}
            </button>
          </div>

          {/* ── Table visual + participant list ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* 3-D Round Table */}
            <div
              className="glass rounded-2xl p-8 flex flex-col items-center"
              data-testid="table-details-visual"
            >
              <p className="overline mb-6 self-start">Seating view</p>
              <RoundTable3D
                seats={seats}
                tableIcon={tableIcon}
                tableName={tableName}
                onSeatClick={handleSeatClick}
              />

              {/* Seat legend */}
              <div className="mt-6 flex items-center gap-4 text-xs text-white/50 self-start">
                <span className="flex items-center gap-1.5">
                  <span className="w-4 h-4 rounded-full border-2 border-white/30 bg-[#C6A87C]/20 inline-block" />
                  Occupied
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-4 h-4 rounded-full border-2 border-dashed border-white/20 inline-block" />
                  Open
                </span>
              </div>
            </div>

            {/* Participant list */}
            <div
              className="glass rounded-2xl p-6"
              data-testid="table-details-participants"
            >
              <div className="flex items-center justify-between mb-4">
                <p className="overline flex items-center gap-2">
                  <Users size={12} aria-hidden="true" /> Participants
                </p>
                <span className="text-xs text-white/40">
                  {filled} / {capacity}
                </span>
              </div>

              <div className="space-y-1">
                {seats.map((occupant, i) =>
                  occupant ? (
                    <ParticipantRow
                      key={getUserId(occupant) ?? i}
                      occupant={occupant}
                      index={i}
                      onSeatClick={handleSeatClick}
                    />
                  ) : (
                    <EmptySeatRow key={`empty-${i}`} index={i} />
                  ),
                )}
              </div>

              {initialLoading && (
                <div className="mt-4 space-y-2" aria-hidden="true">
                  {Array.from({ length: 3 }, (_, i) => (
                    <div
                      key={i}
                      className="h-10 rounded-xl bg-white/[0.03] animate-pulse"
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── Agenda ── */}
          {tableAgenda && (
            <div className="mb-8">
              <AgendaCard agenda={tableAgenda} />
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
